import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { useLocalization } from '../hooks/useLocalization';

const Footer: React.FC = () => {
  const { t } = useLocalization();

  return (
    <footer className="bg-gray-900 dark:bg-gray-800 text-white dark:text-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="space-y-4 min-w-0">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">V</span>
              </div>
              <span className="text-xl font-bold truncate">Evoltbay</span>
            </div>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed break-words">
              {t('footer.description')}
            </p>
            <div className="space-y-2 text-sm text-gray-300 dark:text-gray-400">
              <div className="flex items-center gap-2 min-w-0">
                <Mail size={16} className="flex-shrink-0" />
                <a href="mailto:support@evoltbay.com" className="hover:text-white dark:hover:text-gray-200 transition-colors truncate">support@evoltbay.com</a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-lg font-semibold">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2 text-sm text-gray-300 dark:text-gray-400">
              <li><Link to="/products" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.browseProducts')}</Link></li>
              <li><Link to="/auctions" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.liveAuctions')}</Link></li>
              <li><Link to="/categories" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.categories')}</Link></li>
              <li><Link to="/roi-calculator" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.roiCalculator')}</Link></li>
              <li><Link to="/enterprise" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.enterpriseSolutions')}</Link></li>
            </ul>
          </div>

          {/* For Business */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-lg font-semibold">{t('footer.forBusiness')}</h3>
            <ul className="space-y-2 text-sm text-gray-300 dark:text-gray-400">
              <li><Link to="/sell" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.becomeVendor')}</Link></li>
              <li><Link to="/enterprise" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.enterpriseBuyers')}</Link></li>
              <li><Link to="/gov/register" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.governmentAgencies')}</Link></li>
              <li><Link to="/quotes" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.quoteManagement')}</Link></li>
              <li><Link to="/wallet" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.digitalWallet')}</Link></li>
            </ul>
          </div>

          {/* Legal & Support */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-lg font-semibold">{t('footer.legalSupport')}</h3>
            <ul className="space-y-2 text-sm text-gray-300 dark:text-gray-400">
              <li><Link to="/terms" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.termsOfService')}</Link></li>
              <li><Link to="/privacy" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.privacyPolicy')}</Link></li>
              <li><a href="mailto:support@evoltbay.com" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.contactSupport')}</a></li>
              <li><a href="mailto:legal@evoltbay.com" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.legalInquiries')}</a></li>
              <li><Link to="/dashboard" className="hover:text-white dark:hover:text-gray-200 transition-colors break-words">{t('footer.helpCenter')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Social Media & Bottom Bar */}
        <div className="border-t border-gray-800 dark:border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
            
            {/* Social Media Links */}
            <div className="flex items-center flex-wrap gap-4 justify-center md:justify-start">
              <span className="text-sm text-gray-300 dark:text-gray-400">{t('footer.followUs')}</span>
              <div className="flex space-x-3">
                <a
                  href="https://facebook.com/evoltbay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-200 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://twitter.com/evoltbay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-200 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter size={20} />
                </a>
                <a
                  href="https://linkedin.com/company/evoltbay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-200 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://instagram.com/evoltbay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-200 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-sm text-gray-400 dark:text-gray-500 text-center md:text-right min-w-0">
              <p>© {new Date().getFullYear()} Evoltbay. {t('footer.allRightsReserved')}</p>
              <p className="mt-1 break-words">{t('footer.empoweringSustainable')}</p>
            </div>
          </div>
        </div>

        {/* Additional Legal Notice */}
        <div className="border-t border-gray-800 dark:border-gray-700 mt-6 pt-6">
          <p className="text-xs text-gray-500 dark:text-gray-600 text-center leading-relaxed break-words">
            {t('footer.legalNotice')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 